import React, { useMemo, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Content,
  ProductsHeader,
  ProductsTitle,
  CountBadge,
  LastUpdate,
  InventoryHeaderRow,
  InventoryLeft,
  InventoryRight,
  FilterRow,
  FilterBtn,
  SearchWrap,
  SearchIconWrap,
  SearchInput,
  SearchClear,
  ViewToggle,
  ViewToggleBtn,
  ProductList,
  ProductListRow,
  ProductListLeft,
  ProductThumb,
  ProductListInfo,
  ProductListName,
  ProductListMeta,
  GroupBadge,
  ProductListRight,
  ProductPrice,
  BtnClone,
  BtnView,
  BtnBuy,
  BtnGroup,
  BtnAction,
  TimerBadge,
  AuctionTimerBtn,
  GroupTimerBtn,
  ProductGrid,
  ProductCard,
  ProductCardImage,
  ProductCardImageBadge,
  ProductCardBody,
  ProductCardTitle,
  ProductCardMeta,
  ProductCardActions,
  NoResults,
  LotCard,
  LotCardContent,
  LotCardHeader,
  LotNumber,
  LotProductName,
  AssociatedBadge,
  LotCardMeta,
  LotCardActions,
  BtnTrace,
} from './styled';

function formatEndTime(isoString) {
  if (!isoString) return null;
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return null;
    const day = d.getDate();
    const month = d.toLocaleDateString('en-GB', { month: 'short' });
    const hours = d.getHours();
    const mins = d.getMinutes();
    return `${day} ${month} ${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  } catch {
    return null;
  }
}

/** Returns time remaining until endTime (ISO string). Formats: text = "02d 05:30:45", shortText = "02:05:30:45" (DD:HH:MM:SS) */
function getCountdown(endTimeIso) {
  if (!endTimeIso) return null;
  const end = new Date(endTimeIso).getTime();
  if (Number.isNaN(end)) return null;
  const now = Date.now();
  const ms = Math.max(0, end - now);
  const ss = Math.floor(ms / 1000) % 60;
  const mm = Math.floor(ms / (1000 * 60)) % 60;
  const hh = Math.floor(ms / (1000 * 60 * 60)) % 24;
  const dd = Math.floor(ms / (1000 * 60 * 60 * 24));
  const expired = end - now <= 0;
  const pad = (n) => String(n).padStart(2, '0');
  return {
    dd, hh, mm, ss, expired,
    text: expired ? 'Ended' : `${pad(dd)}d ${pad(hh)}:${pad(mm)}:${pad(ss)}`,
    shortText: expired ? 'Ended' : `${pad(dd)}:${pad(hh)}:${pad(mm)}:${pad(ss)}`,
  };
}

/** Live countdown. format="short" gives DD:HH:MM:SS for timer buttons */
function CountdownDisplay({ endTimeIso, format }) {
  const [countdown, setCountdown] = useState(() => getCountdown(endTimeIso));

  useEffect(() => {
    if (!endTimeIso) return;
    const tick = () => setCountdown(getCountdown(endTimeIso));
    const id = setInterval(tick, 1000);
    tick();
    return () => clearInterval(id);
  }, [endTimeIso]);

  if (!countdown) return null;
  return <>{format === 'short' ? countdown.shortText : countdown.text}</>;
}

function highlightTokens(text, tokens) {
  if (!text || !tokens.length) return text;
  let result = [String(text)];
  tokens.forEach((token) => {
    result = result.flatMap((part) => {
      if (typeof part !== 'string') return part;
      const re = new RegExp(`(${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const pieces = part.split(re);
      return pieces.map((piece, i) =>
        piece.toLowerCase() === token.toLowerCase() ? (
          <span
            key={`${token}-${i}`}
            style={{
              background: 'rgba(0, 247, 210, 0.2)',
              color: '#00f7d2',
              padding: '0 2px',
              borderRadius: '2px',
            }}
          >
            {piece}
          </span>
        ) : (
          piece
        )
      );
    });
  });
  return result;
}

const ProductListRowItem = React.memo(function ProductListRowItem({
  item,
  isSelected,
  searchTokens,
  onSelect,
  onView,
  onClone,
  onBuy,
  onGroup,
  symbol,
  showClone,
  allProducts = [],
  groupsByProductId = {},
}) {
  const history = useHistory();
  const isGroup = item.isGroup || false;
  const isLot = item.isLot || false;
  const image = item.pictures?.[0];
  const name = item.title || (isGroup ? 'Group Purchase' : isLot ? `Lot #${item.lot_number}` : 'Untitled Product');
  const price = isGroup || isLot
    ? null
    : (item.product_sizes_quantity?.[0]?.price ?? item.euro_price?.[0] ?? 0);
  const meta = isGroup
    ? `${item.memberCount ?? 0} members`
    : isLot
    ? `Lot #${item.lot_number}${item.global_trade_item_number ? ` • GTIN: ${item.global_trade_item_number}` : ''}`
    : `ID: ${(item._id || '').toUpperCase()}`;

  const highlightedName = useMemo(
    () => (Array.isArray(searchTokens) ? highlightTokens(name, searchTokens) : name),
    [name, searchTokens]
  );
  const highlightedMeta = useMemo(
    () => (Array.isArray(searchTokens) ? highlightTokens(meta, searchTokens) : meta),
    [meta, searchTokens]
  );

  const groupEndTimeIso = isGroup ? item.end_time : null;
  const auctionEndTimeIso = isGroup
    ? item.product?.[0]?.auction_detail?.[0]?.auction_end_time
    : item.auction_detail?.[0]?.auction_end_time;

  // Lot-specific data extraction
  const lotProduction = isLot && item.production?.[0] ? item.production[0] : null;
  const lotExpiryDate = lotProduction?.expiry_date 
    ? new Date(lotProduction.expiry_date).toISOString().split('T')[0]
    : null;
  const lotQuantity = lotProduction?.quantity ?? null;

  // Check if lot is associated with a product
  const isAssociated = isLot && item.product_id && allProducts.some(p => p._id === item.product_id);

  // For regular products, check if they are part of one or more groups
  const productIdStr = !isGroup && !isLot && (item._id ? String(item._id) : null);
  const groupInfoForProduct = productIdStr ? groupsByProductId[productIdStr] : null;

  // Render lot card with special styling
  if (isLot) {
    return (
      <LotCard
        className={isSelected ? 'selected' : ''}
        onClick={() => onSelect(item)}
      >
        <LotCardContent>
          <LotCardHeader>
            <LotNumber>{highlightTokens(String(item.lot_number || ''), searchTokens || [])}</LotNumber>
            <LotProductName>{highlightedName}</LotProductName>
            {isAssociated && <AssociatedBadge>Associated</AssociatedBadge>}
          </LotCardHeader>
          <LotCardMeta>
            {lotExpiryDate && `Expiry: ${highlightTokens(lotExpiryDate, searchTokens || [])}`}
            {lotExpiryDate && lotQuantity && ' · '}
            {lotQuantity && `Qty: ${highlightTokens(String(lotQuantity), searchTokens || [])}`}
          </LotCardMeta>
        </LotCardContent>
        <LotCardActions>
          <BtnView
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onView(item);
            }}
          >
            View
          </BtnView>
          <BtnTrace
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // TODO: Implement trace functionality
              console.log('Trace lot:', item);
            }}
          >
            Trace
          </BtnTrace>
        </LotCardActions>
      </LotCard>
    );
  }

  // Regular product/group row
  return (
    <ProductListRow
      className={isSelected ? 'selected' : ''}
      onClick={() => onSelect(item)}
    >
      <ProductListLeft>
        {image && <ProductThumb src={image} alt="" />}
        <ProductListInfo>
          <ProductListName>
            {isGroup && <GroupBadge style={{ marginRight: '8px' }}>Group</GroupBadge>}
            {highlightedName}
          </ProductListName>
          <ProductListMeta>{highlightedMeta}</ProductListMeta>
        </ProductListInfo>
      </ProductListLeft>
      <ProductListRight>
        {price != null && (
          <ProductPrice>
            {highlightTokens(String(price), searchTokens || [])} {symbol || 'USDT'}
          </ProductPrice>
        )}
        {/* Clone is only available for regular products, not groups/lots/auctions */}
        {showClone && !isGroup && !isLot && !auctionEndTimeIso && (
          <BtnClone
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClone(item);
            }}
          >
            Clone
          </BtnClone>
        )}
        <BtnView
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView(item);
          }}
        >
          View
        </BtnView>
        {!isGroup && !isLot && (
          <BtnBuy type="button" onClick={(e) => e.stopPropagation()}>
            Buy
          </BtnBuy>
        )}
        {isGroup && groupEndTimeIso && (
          <GroupTimerBtn
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (item._id) {
                history.push(`/groups/${item._id}`);
              }
            }}
          >
            Join Group · <CountdownDisplay endTimeIso={groupEndTimeIso} format="short" />
          </GroupTimerBtn>
        )}
        {!isGroup && !isLot && groupInfoForProduct && groupInfoForProduct.count > 0 && (
          <GroupTimerBtn
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (groupInfoForProduct.groupId) {
                history.push(`/groups/${groupInfoForProduct.groupId}`);
              }
            }}
          >
            Join Group ({groupInfoForProduct.count})
            {groupInfoForProduct.nearestEndTimeIso && (
              <>
                {' · '}
                <CountdownDisplay endTimeIso={groupInfoForProduct.nearestEndTimeIso} format="short" />
              </>
            )}
          </GroupTimerBtn>
        )}
        {auctionEndTimeIso && (
          <AuctionTimerBtn type="button" onClick={(e) => e.stopPropagation()}>
            Auction · <CountdownDisplay endTimeIso={auctionEndTimeIso} format="short" />
          </AuctionTimerBtn>
        )}
      </ProductListRight>
    </ProductListRow>
  );
});

const ProductGridCard = React.memo(function ProductGridCard({
  item,
  isSelected,
  searchTokens,
  onSelect,
  onView,
  onClone,
  onBuy,
  onGroup,
  symbol,
  showClone,
  groupsByProductId = {},
}) {
  const history = useHistory();
  const isGroup = item.isGroup || false;
  const isLot = item.isLot || false;
  const image = item.pictures?.[0];
  const name = item.title || (isGroup ? 'Group Purchase' : isLot ? `Lot #${item.lot_number}` : 'Untitled Product');
  const price = isGroup || isLot
    ? null
    : (item.product_sizes_quantity?.[0]?.price ?? item.euro_price?.[0] ?? 0);
  const meta = isGroup
    ? `${item.memberCount ?? 0} members`
    : isLot
    ? `Lot #${item.lot_number}${item.global_trade_item_number ? ` • GTIN: ${item.global_trade_item_number}` : ''}`
    : `Lot / ID: ${(item._id || '').slice(-8)}`;

  const highlightedName = useMemo(
    () => (Array.isArray(searchTokens) ? highlightTokens(name, searchTokens) : name),
    [name, searchTokens]
  );

  const groupEndTimeIso = isGroup ? item.end_time : null;
  const auctionEndTimeIso = isGroup
    ? item.product?.[0]?.auction_detail?.[0]?.auction_end_time
    : item.auction_detail?.[0]?.auction_end_time;

  const productIdStr = !isGroup && !isLot && (item._id ? String(item._id) : null);
  const groupInfoForProduct = productIdStr ? groupsByProductId[productIdStr] : null;

  return (
    <ProductCard
      className={isSelected ? 'selected' : ''}
      onClick={() => onSelect(item)}
    >
      <ProductCardImage>
        {isGroup && <ProductCardImageBadge>Group</ProductCardImageBadge>}
        {image && <img src={image} alt="" />}
      </ProductCardImage>
      <ProductCardBody>
        <ProductCardTitle>{highlightedName}</ProductCardTitle>
        <ProductCardMeta>{meta}</ProductCardMeta>
        {price != null && (
          <ProductPrice style={{ display: 'block', marginTop: '0.5rem' }}>
            {price} {symbol || 'USDT'}
          </ProductPrice>
        )}
        <ProductCardActions>
          {showClone && (
            <BtnClone
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onClone(item);
              }}
            >
              Clone
            </BtnClone>
          )}
          <BtnView
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onView(item);
            }}
          >
            View
          </BtnView>
          {!isGroup && !isLot && <BtnBuy type="button" onClick={(e) => e.stopPropagation()}>Buy</BtnBuy>}
          {isGroup && groupEndTimeIso && (
            <GroupTimerBtn
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (item._id) {
                  history.push(`/groups/${item._id}`);
                }
              }}
            >
              Join Group · <CountdownDisplay endTimeIso={groupEndTimeIso} format="short" />
            </GroupTimerBtn>
          )}
          {!isGroup && !isLot && groupInfoForProduct && groupInfoForProduct.count > 0 && (
            <GroupTimerBtn
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (groupInfoForProduct.groupId) {
                  history.push(`/groups/${groupInfoForProduct.groupId}`);
                }
              }}
            >
              Join Group ({groupInfoForProduct.count})
              {groupInfoForProduct.nearestEndTimeIso && (
                <>
                  {' · '}
                  <CountdownDisplay endTimeIso={groupInfoForProduct.nearestEndTimeIso} format="short" />
                </>
              )}
            </GroupTimerBtn>
          )}
          {auctionEndTimeIso && (
            <AuctionTimerBtn type="button" onClick={(e) => e.stopPropagation()}>
              Auction · <CountdownDisplay endTimeIso={auctionEndTimeIso} format="short" />
            </AuctionTimerBtn>
          )}
        </ProductCardActions>
      </ProductCardBody>
    </ProductCard>
  );
});

const FILTER_OPTIONS = [
  { value: 'products', label: 'Products' },
  { value: 'groups', label: 'Groups' },
  { value: 'auction', label: 'Auction' },
  { value: 'lots', label: 'Lots' },
];

export default React.memo(function ProductListSection({
  totalCount,
  filteredCount,
  lastBulkUpdate,
  productSearch,
  onSearchChange,
  viewMode,
  onViewModeChange,
  items,
  selectedId,
  onSelectItem,
  onViewProduct,
  onCloneProduct,
  onBuy,
  symbol,
  isStoreOwner,
  filterType = 'products',
  onFilterChange,
  allProducts = [],
  groups = [],
}) {


  const showClone = Boolean(isStoreOwner);

  // Build a lookup map of productId -> { count, nearestEndTimeIso, groupId }
  const groupsByProductId = useMemo(() => {
    const map = {};
    if (!Array.isArray(groups)) return map;
    groups.forEach((g) => {
      const endIso = g.end_time;
      const productDetails = Array.isArray(g.product_details) ? g.product_details : [];
      productDetails.forEach((pd) => {
        const pid = pd.product_id || pd._id;
        if (!pid) return;
        const key = String(pid);
        const existing = map[key] || { count: 0, nearestEndTimeIso: null, groupId: null };
        const endTs = endIso ? new Date(endIso).getTime() : null;
        const currentTs = existing.nearestEndTimeIso ? new Date(existing.nearestEndTimeIso).getTime() : null;
        existing.count += 1;
        if (endTs && (!currentTs || endTs < currentTs)) {
          existing.nearestEndTimeIso = endIso;
          existing.groupId = g._id;
        }
        map[key] = existing;
      });
    });
    return map;
  }, [groups]);
  const searchTokens = useMemo(() => {
    const t = (productSearch || '').trim().toLowerCase();
    return t ? t.split(/\s+/).filter(Boolean) : [];
  }, [productSearch]);

  const hasNoResults = filteredCount === 0;

  return (
    <Content>
      <ProductsHeader>
        <ProductsTitle>Products</ProductsTitle>
        <InventoryHeaderRow>
          <InventoryLeft>
            {onFilterChange && (
              <FilterRow>
                {FILTER_OPTIONS.map((opt) => (
                  <FilterBtn
                    key={opt.value}
                    type="button"
                    $active={filterType === opt.value}
                    $filterValue={opt.value}
                    onClick={() => onFilterChange(opt.value)}
                  >
                    {opt.label}
                  </FilterBtn>
                ))}
              </FilterRow>
            )}
            <CountBadge className={hasNoResults ? 'empty' : ''}>
              {totalCount} total / {filteredCount} shown
            </CountBadge>
            <LastUpdate>Last bulk update: {lastBulkUpdate}</LastUpdate>
          </InventoryLeft>
          <InventoryRight>
            <SearchWrap>
              <SearchIconWrap aria-hidden>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </SearchIconWrap>
              <SearchInput
                type="text"
                placeholder="Search inventory..."
                value={productSearch}
                onChange={(e) => onSearchChange(e.target.value)}
                aria-label="Search inventory"
              />
              {productSearch && (
                <SearchClear type="button" onClick={() => onSearchChange('')}>
                  Clear
                </SearchClear>
              )}
            </SearchWrap>
            <ViewToggle>
              <ViewToggleBtn
                type="button"
                $active={viewMode === 'list'}
                onClick={() => onViewModeChange('list')}
              >
                List
              </ViewToggleBtn>
              <ViewToggleBtn
                type="button"
                $active={viewMode === 'grid'}
                onClick={() => onViewModeChange('grid')}
              >
                Grid
              </ViewToggleBtn>
            </ViewToggle>
          </InventoryRight>
        </InventoryHeaderRow>
      </ProductsHeader>

      {hasNoResults ? (
        <NoResults>No results found.</NoResults>
      ) : viewMode === 'list' ? (
        <ProductList>
          {items.map((item) => (
            <ProductListRowItem
              key={item._id}
              item={item}
              isSelected={selectedId === item._id}
              searchTokens={searchTokens}
              onSelect={onSelectItem}
              onView={onViewProduct}
              onClone={onCloneProduct}
              onBuy={onBuy}
              onGroup={() => {}}
              symbol={symbol}
              showClone={showClone}
              allProducts={allProducts}
            groupsByProductId={groupsByProductId}
            />
          ))}
        </ProductList>
      ) : (
        <ProductGrid>
          {items.map((item) => (
            <ProductGridCard
              key={item._id}
              item={item}
              isSelected={selectedId === item._id}
              searchTokens={searchTokens}
              onSelect={onSelectItem}
              onView={onViewProduct}
              onClone={onCloneProduct}
              onBuy={onBuy}
              onGroup={() => {}}
              symbol={symbol}
              showClone={showClone}
              groupsByProductId={groupsByProductId}
            />
          ))}
        </ProductGrid>
      )}
    </Content>
  );
});
